import React, { ComponentProps, useState, useEffect } from "react";
import { Typography, Button } from "@hegias/ui-components";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import useFetch from "use-http";

import { Modal, Switch } from "components";
import getDomain from "utils/getDomain";
import {
	CREATE_ORGANIZATION,
	ADMIN,
	MEMBERS,
	PROJECTS,
} from "common/routes.constants";
import { useAuth } from "contexts/Auth";
import { useOrganizations } from "contexts/Organizations";
import useTenant from "contexts/Tenant/useTenant";
import {
	WelcomePopupTypes,
	welcomeActionKinds,
	WelcomeActionTypes,
} from "./types";
import contents from "./contents";

interface WelcomeModalProps extends ComponentProps<typeof Modal> {
	visible: boolean;
	currentCase: WelcomePopupTypes;
	onCancel: () => void;
}

const WelcomeModal = ({
	onCancel,
	currentCase,
	...rest
}: WelcomeModalProps) => {
	const { t } = useTranslation();
	const history = useHistory();
	const { search } = useLocation();
	const { me } = useAuth();
	const {
		organizations,
		current,
		setCurrentOrganization,
		member,
	} = useOrganizations();
	const { subdomainsSupported } = useTenant();

	const [userShowPopup, setUserShowPopUp] = useState<boolean>(false);

	const { put } = useFetch("users/me");

	const handleSwitchOfOrg = (route: string) => {
		const nextOrganization = organizations.find(
			(o) => o._id !== process.env.REACT_APP_DEFAULT_ORG,
		);

		if (
			current?.id === process.env.REACT_APP_DEFAULT_ORG &&
			organizations.length === 2
		) {
			if (subdomainsSupported) {
				const domain = getDomain();
				const nextLocation = `${window.location.protocol}//${nextOrganization?.meta.subdomain}.${domain}${route}`;
				window.location.assign(nextLocation);
			}

			setCurrentOrganization(nextOrganization?._id);
		}
	};

	const handleClick = (actionToDo: WelcomeActionTypes) => {
		onCancel();
		const actions = {
			[welcomeActionKinds.CreateOrg]: () =>
				history.push({
					pathname: `/${CREATE_ORGANIZATION}`,
					search,
				}),
			[welcomeActionKinds.VROnBoarding]: () => {
				window.open("https://hegias.com/en/onboarding/", "_blank");
			},
			[welcomeActionKinds.InviteMember]: () => {
				handleSwitchOfOrg(`/${ADMIN}/${MEMBERS}`);

				history.push({
					pathname: `/${ADMIN}/${MEMBERS}`,
					search,
				});
			},
			[welcomeActionKinds.FirstUpload]: () => {
				handleSwitchOfOrg(`/${PROJECTS}`);

				history.push({
					pathname: `/${PROJECTS}`,
					search,
				});
			},
		};

		actions[actionToDo]();
	};

	useEffect(() => {
		if (me) {
			setUserShowPopUp(me.preferences.showIntroPopup);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [me]);

	return (
		<Modal
			onCancel={() => onCancel()}
			title={t(`${contents[currentCase].mainTitle}`)}
			{...rest}
			size="large"
			justifycontent="left"
			footer={
				<StyledSectionContent>
					<div className="switcher-container">
						<Switch
							checked={userShowPopup}
							style={{ marginRight: "10px" }}
							onChange={() => {
								put({
									profile: { email: me?.profile.email },
									preferences: {
										showIntroPopup: !userShowPopup,
									},
								});

								setUserShowPopUp(!userShowPopup);
							}}
						/>
						<div className="switch-text-container">
							<Typography variant="h5" color="grey800">
								<p className="popup-text">
									{t("app.IntroPopup.modal.checkbox")}
								</p>
								<p className="popup-text">
									{t("app.IntroPopup.modal.info")}
								</p>
							</Typography>
						</div>
					</div>
				</StyledSectionContent>
			}
		>
			<StyledPanelInfoContainer style={{ paddingTop: 0 }}>
				<div className="modal-containers">
					<Typography variant="h2" color="primary">
						<p className="containers-text">
							{t(`${contents[currentCase].option1.title}`)}
						</p>
					</Typography>
					<Typography variant="p">
						<p className="containers-text">
							{t(`${contents[currentCase].option1.text}`)}
						</p>
					</Typography>
					<div className="popup-images">
						<img
							src={contents[currentCase].option1.image}
							alt="img"
						/>
					</div>
					<div className="popup-buttons">
						<StyledButton
							disabled={
								contents[currentCase].option1.buttonAction ===
									welcomeActionKinds.InviteMember &&
								member &&
								!["owner", "admin"].includes(member?.role)
							}
							onClick={() =>
								handleClick(
									contents[currentCase].option1.buttonAction,
								)
							}
							type="button"
						>
							{t(`${contents[currentCase].option1.buttonText}`)}
						</StyledButton>
					</div>
				</div>
				<div className="modal-containers">
					<Typography variant="h2" color="primary">
						<p className="containers-text">
							{t(`${contents[currentCase].option2.title}`)}
						</p>
					</Typography>
					<Typography variant="p">
						<p className="containers-text">
							{t(`${contents[currentCase].option2.text}`)}
						</p>
					</Typography>
					<div className="popup-images">
						<img
							src={contents[currentCase].option2.image}
							alt="img"
						/>
					</div>
					<div className="popup-buttons">
						<StyledButton
							onClick={() =>
								handleClick(
									contents[currentCase].option2.buttonAction,
								)
							}
							type="button"
						>
							{t(`${contents[currentCase].option2.buttonText}`)}
						</StyledButton>
					</div>
				</div>
			</StyledPanelInfoContainer>
		</Modal>
	);
};

const StyledPanelInfoContainer = styled.div`
	width: 100%;
	display: flex;
	.modal-containers {
		margin: 10px;
		background: ${({ theme }) => theme.colors.grey400};
		width: 50%;
		padding: 4%;
		display: flex;
		flex-direction: column;
		text-align: center;
	}
	.containers-text {
		padding: 3%;
	}
	.popup-buttons {
		height: 100%;
		display: flex;
		flex-direction: column-reverse;
	}
	.popup-images {
		display: flex;
		width: 70%;
		height: 50%;
		align-self: center;
		justify-content: center;
		padding: 10px;
	}
	@media only screen and (max-width: 600px) {
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		.modal-containers {
			align-self: center;
			padding: 20px;
			width: 100%;
			padding-bottom: 10px;
		}
		.containers-text {
			padding: 0;
			padding-left: 5px;
			padding-right: 5px;
		}
		.popup-images {
			width: 60%;
			height: 50%;
			padding: 5px;
		}
	}
`;

const StyledSectionContent = styled.div`
	.switcher-container {
		background: white;
		display: flex;
		justify-content: left;
		align-items: center;
	}
	.switch-text-container {
		display: flex;
		flex-direction: column;
		text-align: right !important;
	}
	.popup-text {
		text-align: start;
	}
`;
const StyledButton = styled(Button)`
	width: 60%;
	align-self: center;
	padding: 0px;
	@media only screen and (max-width: 600px) {
		width: 70%;
		height: 35px;
	}
`;

export default WelcomeModal;
